import * as angular from "angular";
import * as _ from "lodash";
import { CanvasserTrackingDataProvider } from "../../../../data-provider/canvasser-tracking.dp";
import { DashboardWidgetFilter } from "../../../../models/dashboard-widget-filter.model";
import { PermissionCheck } from "../../../../shared/permission";
import { Dashboard } from "../../../../models/dashboard.model";
import { Campaign } from "../../../../models/campaign.model";
import { CampaignDataProvider } from "../../../../data-provider/campaign.dp";
import { DialogUtils } from "../../../../helper/dialog.helper";

declare const google;

interface UsersGrouped {
    name: string;
    dataType: "campaign" | "division";
    totalUsers: number;
    onlineUsers: number;
    users: any[];
    campaignid?: number;
    divisionid?: number;
    latitude: number;
    longitude: number;
}

class CanvasserTrackingWidgetController implements ng.IController {
    // Bindings
    public filter: DashboardWidgetFilter;
    public dashboard: Dashboard;
    public mode?: string;

    private $scope: ng.IScope;
    private $timeout: ng.ITimeoutService;
    private dataProvider: CanvasserTrackingDataProvider;
    private organisationUserDataProvider: any; // Add this for the dialog refresh
    private dialog: DialogUtils;
    private utils: any;
    public permissionProvider: PermissionCheck;
    private uiGmapGoogleMapApi: any;
    private campaignDataProvider: CampaignDataProvider;
    private modelsUtils: any;

    // View Data (What the template sees)
    public users: any[] = [];
    public campaigns: UsersGrouped[] = [];
    public divisions: UsersGrouped[] = [];
    public markers: any[] = [];

    // UI State
    public isLoading: boolean = true;
    public view: string = "divisions";
    public showMap: boolean = true;
    public selectedCampaign: any = {};
    public selectedDivision: any = {};
    public globalTotalUsers: number = 0;
    public globalOnlineUsers: number = 0;
    public liveMode: "live" | "today" = "live";

    // Data Cache (Source of truth to avoid re-fetching)
    private allUsers: any[] = [];
    private allCampaigns: UsersGrouped[] = [];

    // Map Properties
    public map: any;
    private rawMapInstance: any = null;
    private nativeMarkers: any[] = [];
    private activeCampaign: Campaign;
    private filterDebounce: any;

    // 2. The $inject array (Crucial so minification doesn't break your code)
    static $inject: string[] = [
        "$scope",
        "$timeout",
        "PermissionCheck",
        "uiGmapGoogleMapApi",
        "CampaignDataProvider",
        "CanvasserTrackingDataProvider",
        "OrganisationUserDataProvider", // Added
        "ModelsUtils",
        "DialogUtils",
        "Utils",
    ];

    // 3. The Constructor with explicit assignments
    constructor(
        $scope: ng.IScope,
        $timeout: ng.ITimeoutService,
        PermissionCheck: PermissionCheck,
        uiGmapGoogleMapApi: any,
        CampaignDataProvider: CampaignDataProvider,
        CanvasserTrackingDataProvider: CanvasserTrackingDataProvider,
        OrganisationUserDataProvider: any,
        ModelsUtils: any,
        DialogUtils: DialogUtils,
        Utils: any
    ) {
        this.$scope = $scope;
        this.$timeout = $timeout;
        this.permissionProvider = PermissionCheck;
        this.uiGmapGoogleMapApi = uiGmapGoogleMapApi;
        this.campaignDataProvider = CampaignDataProvider;
        this.dataProvider = CanvasserTrackingDataProvider;
        this.organisationUserDataProvider = OrganisationUserDataProvider;
        this.modelsUtils = ModelsUtils;
        this.dialog = DialogUtils;
        this.utils = Utils;

        this.activeCampaign = this.campaignDataProvider.activeCampaign;
        this.activeCampaign.getLocalStorage();
    }

    $onInit(): void {
        this.initializeFilter();
        this.setupWatchers();
        this.loadMap();
        this.loadData();
        this.$timeout((): void => {
            this.showMap = true;
        }, 1000);
    }

    private initializeFilter(): void {
        if (!this.filter.permissionIds) {
            this.filter.permissionIds = [];
        }
    }

    private setupWatchers(): void {
        /**
         * OPTIMIZATION: Debounce the filter.
         * If a user clicks 3 checkboxes quickly, we only call the API once.
         */
        this.$scope.$watch(
            (): DashboardWidgetFilter => this.filter,
            (newValue: any, oldValue: any): void => {
                if (oldValue && newValue !== oldValue) {
                    if (this.filterDebounce)
                        this.$timeout.cancel(this.filterDebounce);

                    this.filterDebounce = this.$timeout((): void => {
                        this.loadData();
                    }, 300); // Wait 300ms for user to stop clicking
                }
            },
            true
        );
    }

    async loadData(): Promise<void> {
        // OPTIMIZATION: Don't clear this.users immediately.
        // Keep the old data visible until the new data arrives to prevent flickering.
        this.isLoading = true;

        this.dataProvider.filter = this.filter;
        try {
            const usersFromApi: any =
                await this.dataProvider.getAllUsersFromCampaignExtra(
                    this.filter
                );

            // Do the heavy lifting
            this.allUsers = usersFromApi;

            this.applyCurrentFilters();

            this.$timeout((): void => {
                this.isLoading = false;
                this.showMap = true; // Show map only when data is ready
            });
        } catch (error) {
            this.isLoading = false;
            console.error("Data load failed", error);
        }
    }

    /**
     * OPTIMIZATION: Marker Batching
     * We use requestAnimationFrame to prevent the browser from locking up
     */
    private updateNativeMapMarkers(): void {
        if (!this.rawMapInstance) return;

        // Clear existing
        this.nativeMarkers.forEach((marker: any): any => marker.setMap(null));
        this.nativeMarkers = [];

        if (!this.markers || this.markers.length === 0) return;

        const bounds = new google.maps.LatLngBounds();

        // We process markers in a single block, but avoid excessive UI updates
        this.markers.forEach((markerData: any): any => {
            if (!markerData.latitude || !markerData.longitude) return;

            const position = {
                lat: Number(markerData.latitude),
                lng: Number(markerData.longitude),
            };

            const googleMarker = new google.maps.Marker({
                position: position,
                map: this.rawMapInstance,
                icon: markerData.options.icon,
                optimized: this.markers.length > 100,
            });

            googleMarker.addListener("click", (): void => {
                this.$timeout((): void => this.onMarkerClick(markerData));
            });

            googleMarker.addListener("mouseover", (): void => {
                googleMarker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
            });

            googleMarker.addListener("mouseout", (): void => {
                googleMarker.setZIndex(null);
            });

            bounds.extend(position);
            this.nativeMarkers.push(googleMarker);
        });

        if (this.nativeMarkers.length > 0) {
            const minAutoZoom = 15;
            this.rawMapInstance.fitBounds(bounds);

            // Limit zoom level
            const idleListener: any = google.maps.event.addListener(
                this.rawMapInstance,
                "idle",
                (): void => {
                    if (this.rawMapInstance.getZoom() > minAutoZoom)
                        this.rawMapInstance.setZoom(minAutoZoom);
                    google.maps.event.removeListener(idleListener);
                }
            );
        }
    }

    /**
     * Replaces the old 60-line grouping logic with a cleaner Lodash flow
     * Handles items without a division by placing them in a fallback group.
     */
    private groupUsersByAttribute(
        users: any[],
        attributeKey: string
    ): UsersGrouped[] {
        const fallbackName = "Sub-accounts without division";

        const groups: any = _.groupBy(users, (user: any): any => {
            const value: any = user[attributeKey];

            // 1. Logic for identifying the name of the group
            if (attributeKey === "divisionname") {
                if (
                    !value ||
                    value === "" ||
                    value === "null" ||
                    value === "undefined"
                ) {
                    return fallbackName;
                }
            }
            return value || "Unknown";
        });

        return _.map(
            groups,
            (groupUsers: any[], groupName: string): UsersGrouped => {
                const usersWithCoordinates: any[] = groupUsers.filter(
                    (user: any): boolean =>
                        user.latitude &&
                        user.longitude &&
                        Number(user.latitude) !== 0
                );

                // 2. NORMALIZATION: Extract IDs and force null/undefined/empty to 0
                // We do this for both Campaign and Division IDs
                let currentDivisionId: any =
                    groupUsers[0] && groupUsers[0].divisionid
                        ? groupUsers[0].divisionid
                        : 0;
                let currentCampaignId: any =
                    groupUsers[0] && groupUsers[0].campaignid
                        ? groupUsers[0].campaignid
                        : 0;

                // Force ID to 0 if the group name is our fallback name
                if (groupName === fallbackName) {
                    currentDivisionId = 0;
                }

                return {
                    name: groupName,
                    dataType:
                        attributeKey === "campaignname"
                            ? "campaign"
                            : "division",
                    totalUsers: groupUsers.length,
                    onlineUsers: groupUsers.filter((user: any): any =>
                        this.isUserActive(user)
                    ).length,
                    users: groupUsers,
                    campaignid: currentCampaignId,
                    divisionid: currentDivisionId,
                    latitude:
                        _.meanBy(usersWithCoordinates, (user: any): number =>
                            Number(user.latitude)
                        ) || 0,
                    longitude:
                        _.meanBy(usersWithCoordinates, (user: any): number =>
                            Number(user.longitude)
                        ) || 0,
                } as UsersGrouped;
            }
        );
    }

    /**
     * Coordinates the relationship between selected divisions/campaigns and the displayed markers
     */
    applyCurrentFilters(): void {
        // 1. Totals
        this.allCampaigns = this.groupUsersByAttribute(this.allUsers, "campaignname");
        this.globalTotalUsers = this.allUsers.length;
        this.globalOnlineUsers = this.allUsers.filter((user: any): any =>
            this.isUserActive(user)
        ).length;

        // 2. Filter Users by Campaign
        // Check if the property 'campaignid' exists in the selected object
        const hasCampaignSelection: any =
            this.selectedCampaign.hasOwnProperty("campaignid");
        this.users = !hasCampaignSelection
            ? [...this.allUsers]
            : this.allUsers.filter((user: any): boolean => {
                  return (
                      (user.campaignid || 0) ===
                      this.selectedCampaign.campaignid
                  );
              });

        // 3. Filter Campaigns by Division
        // Check if the property 'divisionid' exists in the selected object
        const hasDivisionSelection: any =
            this.selectedDivision.hasOwnProperty("divisionid");
        this.campaigns = !hasDivisionSelection
            ? [...this.allCampaigns]
            : this.allCampaigns.filter((campaign: UsersGrouped): boolean => {
                  // Normalize: treat null/undefined as 0 during comparison
                  const campaignDivId = campaign.divisionid || 0;
                  return campaignDivId === this.selectedDivision.divisionid;
              });

        this.divisions = this.groupUsersByAttribute(
            this.allUsers,
            "divisionname"
        );
        this.refreshMarkers();
    }

    openViewUserDialog(uid: number, ev: any = null): void {
        this.dialog.show({
            component: "vc-view-organisation-user",
            targetEvent: ev,
            size: "large",
            scope: this.$scope,
            ariaLabel: "View Person",
            locals: {
                selectedUser: null,
                selectedUserId: uid,
                editable: true,
                onUserDeleted: this.utils.bindFunction((): void => {
                    this.organisationUserDataProvider.clearAll();
                    this.loadData();
                }, this),
                onUserUpdated: this.utils.bindFunction((): void => {
                    this.organisationUserDataProvider.clearAll();
                    this.loadData();
                }, this),
            },
        });
    }

    loadMap() {
        this.uiGmapGoogleMapApi.then((): void => {
            this.map = {
                center: this.activeCampaign.localInfo.map?.center || {
                    latitude: 20,
                    longitude: 4,
                },
                zoom: this.activeCampaign.localInfo.map ? 8 : 4,
                options: {
                    disableDefaultUI: true,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    zoomControl: false,
                },
                events: {
                    tilesloaded: (mapInstance: any): void => {
                        if (!this.rawMapInstance) {
                            this.rawMapInstance = mapInstance;
                            if (this.markers.length > 0)
                                this.updateNativeMapMarkers();
                        }
                    },
                },
            };
        });
    }

    refreshMarkers(): void {
        this.dataProvider.clearMarkers(this.markers);

        let activeDataSource: any[];

        if (
            (this.view === "campaigns" || this.selectedDivision.name) &&
            !this.selectedCampaign.campaignid
        ) {
            activeDataSource = this.campaigns;
        } else if (this.view === "divisions" && !this.selectedDivision.name) {
            activeDataSource = this.divisions;
        } else {
            activeDataSource = this.users;
        }

        this.markers = this.dataProvider.setMarkers([], activeDataSource);
        this.updateNativeMapMarkers();
    }

    // --- UI INTERACTION HANDLERS ---

    onMarkerClick(markerData: any): void {
        if (markerData.dataType === "campaign") {
            this.selectCampaign(markerData);
        } else if (markerData.dataType === "division") {
            this.selectDivision(markerData);
        } else {
            this.openViewUserDialog(markerData.uid);
        }
    }

    selectCampaign(campaign: any): void {
        _.merge(this.selectedCampaign, campaign);
        this.applyCurrentFilters();
    }

    selectDivision(division: any): void {
        _.merge(this.selectedDivision, division);
        this.applyCurrentFilters();
    }

    unsetCampaign(): void {
        this.selectedCampaign = {};
        this.applyCurrentFilters();
    }

    unsetDivision(): void {
        this.selectedDivision = {};
        this.applyCurrentFilters();
    }

    generateLink(user: any): string {
        const hasPermission = this.permissionProvider.hasPermission(
            "CANVASSER_TRACKING_ALLOWED"
        );
        const isUserContext = this.dashboard.location === "user";

        if (hasPermission || isUserContext) {
            return `/#/canvasser/activity?user=${user.uid}`;
        }
        return `/#/user/${user.uid}`;
    }

    setLiveMode(mode: "live" | "today"): void {
        this.liveMode = mode;
        this.applyCurrentFilters();
    }

    private isUserActive(user: any): boolean {
        return this.liveMode === "today" ? user.isActiveToday() : user.isOnline();
    }
}

export const LeaderCanvasserTrackingWidgetComponent = {
    selector: "vcLeaderCanvasserTrackingWidget",
    templateUrl:
        "/angular/components/canvasser-tracking/widget/leader/leader-canvasser-tracking-widget.component.html",
    bindings: {
        filter: "=",
        dashboard: "=",
        mode: "=?",
    },
    controller: CanvasserTrackingWidgetController,
};

angular
    .module("canvasserTracking")
    .component(
        LeaderCanvasserTrackingWidgetComponent.selector,
        LeaderCanvasserTrackingWidgetComponent
    );
